import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import classNames from "classnames";
import { hasProperty } from "helpers/object";
import has from "lodash/has";
import isEmpty from "lodash/isEmpty";
import Permission from "components/hoc/Permission";
import { ADD_COMMENTS } from "helpers/role";
import { updateFile } from "store/actions/fileAction";
import { getSelectedClient } from "store/actions/clientAction";
import Notification, { notifyApiError } from "components/Notification";
import SubmitButton from "components/Forms/Buttons/Submit";
import ErrorLabel from "components/Forms/Elements/ErrorLabel";
import Translate from "helpers/Translate";
import {
  STATUS_APPROVED,
  STATUS_RESENT_FOR_APPROVAL,
  STATUS_REJECTED,
  STATUS_READY_TO_UPLOAD,
  STATUS_DISCUSSIONS,
  STATUS_UPLOADED
} from "helpers/fileStatus.json";
import { APPROVE, UPLOAD_DOCS_TO_ADAPTER } from "helpers/permissions.json";

export const ReadOnly = ({ isHidden }) => {
  const dispatch = useDispatch();
  const fileInfo = useSelector(state => state.file.getFile);
  const isLoading = useSelector(state => state.file.isLoadingFile);
  const updateFileError = useSelector(state => state.file.errorFile);
  const selectedClient = useSelector(state => state.client.selectedClient);
  const permissions = useSelector(state => state.role.getPermissions);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState(defaultSelectedAction());
  const [escalate, setEscalate] = useState(false);
  const [falseReject, setFalseReject] = useState(false);

  // load the selected client
  useEffect(() => {
    if (!selectedClient) {
      dispatch(getSelectedClient());
    }
  }, [selectedClient, dispatch]);

  // Show form submissions errors (if any)
  useEffect(() => {
    if (updateFileError) {
      notifyApiError(updateFileError);
    }
  }, [updateFileError]);

  // setFalseReject
  useEffect(() => {
    if (fileInfo) {
      setFalseReject(fileInfo.is_false_reject);
    }
  }, [fileInfo]);

  const handleSubmit = e => {
    e.preventDefault();
    if (status === STATUS_REJECTED) {
      handleAllowFileDelete(doSubmit);
    } else {
      doSubmit();
    }
  };

  function doSubmit({ allowFileDelete = false } = {}) {
    dispatch(
      updateFile({
        fileId: fileInfo.id,
        data: {
          status,
          allow_file_delete: allowFileDelete || false,
          is_false_reject: falseReject,
          ignore_approval_rules: escalate,
          comment
        }
      })
    );
  }

  const handleFalseReject = e => {
    if (e.target.checked) {
      if (fileInfo && fileInfo.status === STATUS_REJECTED) {
        setStatus(STATUS_RESENT_FOR_APPROVAL);
      } else {
        setStatus(STATUS_APPROVED);
      }
    }
    setFalseReject(e.target.checked);
  };

  const isMarkedAsFalseRejection = () => {
    return has(fileInfo, "is_false_reject") && fileInfo.is_false_reject;
  };

  function canUploadToAdapter() {
    return (
      fileInfo?.metadata?.client_approval_required === false ||
      (permissions && permissions.includes(UPLOAD_DOCS_TO_ADAPTER))
    );
  }

  function isClient() {
    return permissions && permissions.includes(UPLOAD_DOCS_TO_ADAPTER);
  }

  function defaultSelectedAction() {
    let action = null;
    if (canUploadToAdapter()) {
      action = STATUS_READY_TO_UPLOAD;
    } else if (fileInfo && fileInfo.status === STATUS_REJECTED) {
      action = STATUS_RESENT_FOR_APPROVAL;
    } else {
      action = STATUS_APPROVED;
    }
    return action;
  }

  const canApprove = () => {
    return (
      permissions &&
      permissions.includes(APPROVE) &&
      !permissions.includes(UPLOAD_DOCS_TO_ADAPTER)
    );
  };

  const isChatReplyPending = () => {
    if (has(fileInfo, "chat_reply_pending")) {
      return fileInfo.chat_reply_pending === true;
    }
    return false;
  };

  const canMarkAsFalseRejection = () => {
    return (
      fileInfo &&
      [STATUS_REJECTED, STATUS_DISCUSSIONS].includes(fileInfo.status)
    );
  };

  // Can the user add comments or submit form?
  const isActionable = () => {
    if (fileInfo && fileInfo.status === STATUS_UPLOADED) {
      return false;
    }
    return true;
  };

  const handleAllowFileDelete = done => {
    // The reviewer can allow/disallow the DEO to be able to delete the rejected file
    Notification.confirm(
      Translate({
        id: "a_file_allowed_to_be_deleted_cannot_be_resent_for_approval_also_it_does_not_increment_the_rejection_count"
      }),
      {
        title: Translate({
          id: "allow_desktop_users_to_delete_rejected_files"
        }),
        onOk: () => {
          done({ allowFileDelete: true });
        },
        onCancel: () => {
          done({ allowFileDelete: false });
        },
        labelOk: Translate({ id: "yes" }),
        labelCancel: Translate({ id: "no" })
      }
    );
  };

  return (
    <div
      className={classNames("tab-content tab-content-one selected", {
        hide: isHidden
      })}
    >
      <ReactTooltip />
      <div className="label">{Translate({ id: "document_type" })}:</div>
      <div
        className="readonly-value mt-5"
        title={hasProperty(() => fileInfo.metadata.name, "N/A")}
      >
        {hasProperty(() => fileInfo.metadata.title, "N/A")}
      </div>
      <hr className="my-20" />
      {has(fileInfo, "user_data") &&
        fileInfo.user_data.map((userData, idx) => (
          <div key={idx} className="my-20">
            <div className="label" title={userData.name}>
              {userData.label}
            </div>
            <div className="readonly-value my-5">
              {Array.isArray(userData.user_entered_value)
                ? userData.user_entered_value.join(", ")
                : userData.user_entered_value}
            </div>
          </div>
        ))}

      {has(fileInfo, "relative_path") && (
        <div className="my-20">
          <div className="label">{Translate({ id: "folder_path" })} :</div>
          <div className="readonly-value my-5">{fileInfo.relative_path}</div>
        </div>
      )}

      {/* Show Comments */}
      <div className="my-20">
        <div className="label">{Translate({ id: "latest_comment" })} </div>
        <div className="readonly-value my-5">
          {hasProperty(() => fileInfo.comment, "N/A")}
        </div>
      </div>

      {has(fileInfo, "metadata.data") && isEmpty(fileInfo.metadata.data) && (
        <h6>{Translate({ id: "latest_comments" })} </h6>
      )}
      <hr className="my-20" />

      {isChatReplyPending() ? (
        <div className="alert alert-info">
          <p>{Translate({ id: "a_chat_reply_is_pending" })}.</p>
          <p className="mt-5">
            {Translate({ id: "no_other_action_can_be_taken_at_this_point" })}.
          </p>
        </div>
      ) : (
        <>
          <div
            className={classNames("form-group", {
              hide: !isActionable()
            })}
          >
            <Permission
              message={Translate({
                id: "you_do_not_have_permission_to_add_a_comment"
              })}
              resources={[ADD_COMMENTS]}
            >
              <label>{Translate({ id: "add_comment" })} </label>
              <textarea
                className="form-input"
                id=""
                cols="30"
                rows="40"
                placeholder={Translate({ id: "type_your_message" })}
                onChange={e => setComment(e.target.value)}
              ></textarea>
            </Permission>

            {isMarkedAsFalseRejection() && (
              <div className="form-group my-10">
                <div className="alert alert-info">
                  {Translate({ id: "file_marked_as_false_reject" })}
                </div>
              </div>
            )}

            <div className="form-group my-10">
              <label htmlFor="subscription">
                {Translate({ id: "action" })}{" "}
              </label>
              <div className="form-input-vgroup">
                {canApprove() && (
                  <label className="input-item">
                    <input
                      type="radio"
                      name="action"
                      className="form-input"
                      checked={[
                        STATUS_APPROVED,
                        STATUS_RESENT_FOR_APPROVAL
                      ].includes(status)}
                      onChange={e => {
                        if (fileInfo && fileInfo.status === STATUS_REJECTED) {
                          setStatus(STATUS_RESENT_FOR_APPROVAL);
                        } else {
                          setStatus(STATUS_APPROVED);
                        }
                      }}
                    />{" "}
                    {fileInfo && fileInfo.status === STATUS_REJECTED
                      ? Translate({ id: "resend_for_approval" })
                      : Translate({ id: "approve" })}
                    <i
                      data-tip={Translate({
                        id: "send_to_superordinate_for_approval"
                      })}
                      className="fas fa-info-circle ml-10"
                    ></i>
                  </label>
                )}
                {canUploadToAdapter() && (
                  <label className="input-item">
                    <input
                      type="radio"
                      name="action"
                      className="form-input"
                      checked={status === STATUS_READY_TO_UPLOAD}
                      onChange={e => {
                        setEscalate(false);
                        setStatus(STATUS_READY_TO_UPLOAD);
                      }}
                    />{" "}
                    {Translate({ id: "add_to_upload_queue" })}
                  </label>
                )}

                {/* Reject the file */}
                <label className="input-item">
                  <input
                    type="radio"
                    name="action"
                    className="form-input"
                    checked={status === STATUS_REJECTED}
                    onChange={e => {
                      setEscalate(false);
                      setStatus(STATUS_REJECTED);
                      if (e.target.checked) {
                        setFalseReject(false);
                      }
                    }}
                  />{" "}
                  {Translate({ id: "reject" })}
                </label>

                {/* Escalate to the Superodinate */}
                {!isClient() &&
                  [STATUS_APPROVED, STATUS_RESENT_FOR_APPROVAL].includes(
                    status
                  ) &&
                  fileInfo?.metadata?.client_approval_required === false && (
                    <label className="input-item mt-20">
                      <ReactTooltip />
                      <input
                        type="checkbox"
                        name="escalate"
                        className="form-input"
                        checked={escalate}
                        onChange={e => setEscalate(e.target.checked)}
                      />{" "}
                      {Translate({ id: "escalate" })}{" "}
                      <i>({Translate({ id: "optional" })} )</i>
                      <i
                        data-tip={Translate({
                          id: "send_this_document_for_approval_ignoring_approval_percentage_rules"
                        })}
                        className="fas fa-info-circle ml-10"
                      ></i>
                    </label>
                  )}

                {/* Mark as false Rejection */}
                {canMarkAsFalseRejection() && (
                  <label className="input-item">
                    <input
                      type="checkbox"
                      name="false_reject"
                      className="form-input"
                      checked={falseReject}
                      onChange={e => handleFalseReject(e)}
                    />{" "}
                    {Translate({ id: "false_reject" })}{" "}
                    <i>({Translate({ id: "optional" })} )</i>
                  </label>
                )}
              </div>
              <ErrorLabel
                errorKey="status"
                errors={updateFileError}
                formErrorClass="form-error-inverse"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div
            className={classNames("form-group my-30 pb-20", {
              hide: !isActionable()
            })}
          >
            <SubmitButton
              className="btn btn-tools"
              buttonIconClass="fas fa-paper-plane"
              buttonText={Translate({ id: "save_changes" })}
              btnId="create"
              data-test="btn-save"
              isLoading={isLoading}
              handleSubmit={handleSubmit}
            />
          </div>
          <div className="my-50">&nbsp;</div>
        </>
      )}
    </div>
  );
};

ReadOnly.propType = {
  isHidden: PropTypes.bool
};

export default ReadOnly;
